import { yupResolver } from '@hookform/resolvers/yup';
import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/framework7-types';
import { FC, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import style from './CoatingEditPageStyle.module.css';
import { fetchCoatingUnits } from '@/api/coatingApi';
import { changePrefferedDateCarWash } from '@/api/reservationApi';
import { Button } from '@/components/uiParts/Button';
import { DateTimeSelect } from '@/components/uiParts/DateTimeSelect/DateTimeSelect';
import { PopupPageTemplate } from '@/components/uiParts/Template/PopupPageTemplate/PopupPageTemplate';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { COATING_MIN_DAYS_AHEAD } from '@/consts/coating';
import { CoatingUnitItem } from '@/types/api/coatingApi';
import { Reservation } from '@/types/api/reservationApi';
import { generateCoatingTimeSlots } from '@/utils/coatingTimeSlots';
import { f7CustomBack } from '@/utils/utils';

type CoatingEditPageProps = {
  f7router: Router.Router;
  id?: string;
};

type FormInput = {
  datetime1?: string;
  datetime2?: string;
  datetime3?: string;
};

const uniqueDateCheck: yup.TestFunction<string | undefined, {}> = (
  value,
  context,
) => {
  const { datetime1, datetime2, datetime3 } = context.parent;
  switch (context.path) {
    case 'datetime1':
      return value !== datetime2 && value !== datetime3;
    case 'datetime2':
      if (!value) return true;
      return value !== datetime1 && value !== datetime3;
    case 'datetime3':
      if (!value) return true;
      return value !== datetime1 && value !== datetime2;
  }
  return false;
};

const schema = yup.object().shape({
  // 新規予約と同条件：第一〜第三希望すべて必須かつ重複不可
  datetime1: yup.string().required().test(uniqueDateCheck),
  datetime2: yup.string().required().test(uniqueDateCheck),
  datetime3: yup.string().required().test(uniqueDateCheck),
});

export const CoatingEditPage: FC<CoatingEditPageProps> = ({ id }) => {
  const reservation = useStore(store, 'getReservation') as
    | Reservation
    | undefined;

  const [coatingUnits, setCoatingUnits] = useState<CoatingUnitItem[]>([]);
  const [mUnitId, setMUnitId] = useState<number | undefined>();
  const [workingMinutes, setWorkingMinutes] = useState<number>(0);

  // 予約詳細取得＋店舗一覧取得
  useEffect(() => {
    if (!reservation?.reservation_id) return;
    fetchCoatingUnits().then((res) => {
      setCoatingUnits(res.data.data.units);
    });
  }, [reservation?.reservation_id]);

  // 予約済の店舗ID と所要時間を取得（reservation 詳細から推定）
  useEffect(() => {
    // reservation には基本情報しかないので、改めて detail API を呼ぶ
    // ただし簡略化：useFetchReservationDetail と同等のAPIを叩く
    // 既存のreservation.detail には m_unit_id / working_minutes が含まれていないので
    // 暫定で reservation_id から detail APIで取得する
    if (!reservation?.reservation_id) return;
    import('@/api/reservationApi').then(({ fetchReservationDetail }) => {
      fetchReservationDetail({
        reservation_id: reservation.reservation_id,
        service_type: 'coating',
      }).then((res) => {
        const data = res.data?.data;
        // eslint-disable-next-line no-console
        console.log('[CoatingEdit] reservation detail:', data);
        if (data?.m_unit_id) setMUnitId(data.m_unit_id);
        // 所要時間はcoating_item と coating_option の working_minutes 合計
        const itemMin = data?.coating_item?.working_minutes ?? 60;
        const optionMin = (data?.coating_option ?? []).reduce(
          (sum, op) => sum + (op.working_minutes ?? 0),
          0,
        );
        // eslint-disable-next-line no-console
        console.log(
          '[CoatingEdit] m_unit_id:',
          data?.m_unit_id,
          'workingMinutes:',
          itemMin + optionMin,
        );
        setWorkingMinutes(itemMin + optionMin);
      });
    });
  }, [reservation?.reservation_id]);

  const selectedUnit = useMemo(
    () => coatingUnits.find((u) => u.m_unit_id === mUnitId),
    [coatingUnits, mUnitId],
  );

  const timeSlotOptions = useMemo(
    () =>
      generateCoatingTimeSlots(
        selectedUnit?.start_hm,
        selectedUnit?.end_hm,
        workingMinutes,
      ),
    [selectedUnit, workingMinutes],
  );

  const formMethods = useForm<FormInput>({
    mode: 'onChange',
    resolver: yupResolver(schema) as any,
  });
  const {
    watch,
    handleSubmit,
    formState: { errors, isValid },
  } = formMethods;

  const handleSubmitEdit: SubmitHandler<FormInput> = async (data) => {
    f7.preloader.show();
    if (!data.datetime1) {
      f7.preloader.hide();
      return;
    }
    const rs = await changePrefferedDateCarWash({
      reservation_id: Number(id),
      service_type: 'coating',
      preferred_datetimes: {
        datetime1: data.datetime1,
        datetime2: data.datetime2,
        datetime3: data.datetime3,
      },
    });

    if (rs.data.success) {
      f7CustomBack(['#coatingEdit']);
      f7.views.main.router.navigate(paths.coatingEditSuccess('edit'));
    } else {
      f7.dialog.alert(
        '',
        rs.data.error_message || '希望日の変更に失敗しました',
      );
    }
    f7.preloader.hide();
  };

  const datetime1 = watch('datetime1');
  const datetime2 = watch('datetime2');

  return (
    <PopupPageTemplate id="coatingEdit" pageName="coatingEdit" title="日程変更">
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(handleSubmitEdit)}
          className={style['content-block']}
        >
          <div className={style['label']}>
            ご予約のご希望のお日にち・お時間を選択してください
          </div>
          {timeSlotOptions.length === 0 ? (
            <div style={{ padding: '16px', color: '#707070' }}>
              店舗情報を読み込んでいます...
            </div>
          ) : (
            <>
              <DateTimeSelect
                label="第一希望"
                name="datetime1"
                showError={!!datetime1 && !!errors.datetime1}
                required
                minDaysAhead={COATING_MIN_DAYS_AHEAD}
                timeOptions={timeSlotOptions}
              />
              <DateTimeSelect
                label="第二希望"
                name="datetime2"
                showError={
                  !!errors.datetime2 && (!errors.datetime1 || !datetime1)
                }
                minDaysAhead={COATING_MIN_DAYS_AHEAD}
                timeOptions={timeSlotOptions}
              />
              <DateTimeSelect
                label="第三希望"
                name="datetime3"
                showError={
                  !!errors.datetime3 && (!errors.datetime2 || !datetime2)
                }
                minDaysAhead={COATING_MIN_DAYS_AHEAD}
                timeOptions={timeSlotOptions}
              />
            </>
          )}
          <div className={style['btn-submit']}>
            <Button
              text="上記内容で再予約する"
              type="submit"
              disabled={!isValid}
            />
          </div>
        </form>
      </FormProvider>
    </PopupPageTemplate>
  );
};
